import { UltrabufEntries } from '@tencent/slide-sdk-pb-model';
import { getPresentationKeyframe } from '../keyframe/get-presentation-keyframe';
import { getSlideFileKeyframe } from '../keyframe/get-slide-file-keyframe';
import { SlideCodec } from './slide-codec';
import { RevisionData } from './revision-data';
import { CommandData } from './command-data';
import type { ISlideFileKeyframe } from '../keyframe/get-slide-file-keyframe';
import type { Mutation } from '../mutation/base/base-mutation';
import type { IPresentationData } from '../data';
import type { MarkRequired } from 'ts-essentials';
import type { ICommandData, IMutationData } from '@tencent/slide-sdk-pb-model';

export type RevisionBase64 = string | Uint8Array;
export type CommandBase64 = string | Uint8Array;

export interface ISlideFileData {
    presentation: RevisionBase64;
    pages?: Record<string, RevisionBase64>;
    revisions?: RevisionBase64[];
}

export class SlideFile {
    public static fromCommandData(command: ICommandData): SlideFile {
        return new SlideFile({
            presentation: RevisionData.fromCommandData([command]).toBase64String(),
        });
    }

    public static fromPresentationData(presentation: IPresentationData, usePage?: boolean): SlideFile {
        if (!usePage) {
            return this.fromCommandData(
                CommandData.fromValidMutationData(getPresentationKeyframe(presentation)).getCommandData(),
            );
        }

        const slideFileKeyframe = getSlideFileKeyframe(presentation);
        return SlideFile.fromSlideFileKeyframe(slideFileKeyframe);
    }

    public static fromEGChunkDataBuffer(chunkBuffer: Uint8Array): SlideFile {
        const data = SlideCodec.codec.decode(chunkBuffer, UltrabufEntries.EGChunkData);
        return new SlideFile({
            presentation: data.slideChunkData?.presentation ?? RevisionData.empty.toBase64String(),
            pages: Object.fromEntries(
                (data.slideChunkData?.pages ?? []).map((page) => [
                    page.id ?? '',
                    page.data ?? RevisionData.empty.toBase64String(),
                ]),
            ),
        });
    }

    public static fromSlideFileKeyframe(slideFileKeyframe: ISlideFileKeyframe) {
        return new SlideFile({
            presentation: RevisionData.fromCommandData([
                CommandData.fromValidMutationData(slideFileKeyframe.presentation).getCommandData(),
            ]).toBase64String(),
            pages: Object.fromEntries(
                Object.entries(slideFileKeyframe.pages).map(([id, muts]) => [
                    id,
                    RevisionData.fromCommandData([
                        CommandData.fromValidMutationData(muts).getCommandData(),
                    ]).toBase64String(),
                ]),
            ),
        });
    }

    public static fromMutations(mutations: Mutation[]) {
        return this.fromCommandData(CommandData.fromMutations(mutations).getCommandData());
    }

    public constructor(private data: ISlideFileData) {}

    public getData(): ISlideFileData {
        return this.data;
    }

    public getCommandData(): MarkRequired<ICommandData, 'mutations'> {
        const mutations: IMutationData[] = [];
        const pages = this.data.pages ?? {};
        const revisions = this.data.revisions ?? [];
        this.appendRevisionToMutations(mutations, this.data.presentation);

        for (const [pageId, page] of Object.entries(pages)) {
            // 释放内存
            pages[pageId] = '';
            this.appendRevisionToMutations(mutations, page);
        }

        for (const revision of revisions) {
            // 释放内存
            this.appendRevisionToMutations(mutations, revision);
        }

        return { mutations };
    }

    private appendRevisionToMutations(mutations: IMutationData[], revision: RevisionBase64) {
        for (const mut of RevisionData.fromBufferOrBase64(revision).getMutationData()) {
            mutations.push(mut);
        }
    }
}
